import { Fragment } from 'react';

import styled, { CSSObject } from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { PopoverButton, PopoverButtonProps } from '@components/popover-button';
import { Stack } from '@components/stack';
import { backgroundSlide } from '@styles/animation';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { AppliedFilter } from './applied-filter';
import {
  AppliedFilterData,
  FilterData,
  FilterState,
  FilterConfig,
  SelectableFilterConfigField,
  SeparatorConfig,
} from './common';
import { FILTER_POPOVER_MAX_WIDTH } from './constants';
import { FilterContent } from './filter-content';
import { useFilterTranslate } from './filter-utils';

import { FilterActions } from '../table-store';

export const Filters = ({
  state,
  actions,
  config,
  addFilterText,
  isLoading,
}: {
  state: FilterState;
  actions: FilterActions;
  addFilterText: string;
  config: FilterConfig;
  isLoading?: boolean;
}) => {
  if (!state.isOpen) return null;

  if (state.appliedFilters.length === 0) {
    return (
      <FilterPopover
        actions={actions}
        config={config}
        buttonProps={{
          iconLeft: <IconAddPlus />,
          text: addFilterText,
          size: 'sm',
          variant: 'secondary',
        }}
      />
    );
  }

  return (
    <Stack orientation="horizontal" space={2} style={{ minWidth: 0 }}>
      {isLoading ? (
        <AnimatedFilterSkeleton />
      ) : (
        <AppliedFiltersList
          state={state}
          actions={actions}
          config={config}
          appliedFilters={state.appliedFilters as Array<AppliedFilterData>}
        />
      )}
    </Stack>
  );
};

const AppliedFiltersList = ({
  appliedFilters,
  state,
  actions,
  config,
}: {
  appliedFilters: Array<AppliedFilterData>;
  actions: FilterActions;
  state: FilterState;
  config: FilterConfig;
}) => {
  const { t } = useFilterTranslate();
  return (
    <Fragment>
      {appliedFilters.map((filter, index) => {
        const filterConfig = config.find((cfg) => cfg.id === filter.category);
        if (!filterConfig) return null;

        return (
          <Stack orientation="horizontal" space={2} key={'filter-' + filter.id} align="center">
            {index > 0 && (
              <Button
                size="sm"
                variant="secondary"
                onClick={actions.switchConjunction}
                text={t(`core.${state.conjunction}`)}
                style={{ textTransform: 'capitalize' }}
              />
            )}
            <Stack space={2} orientation="horizontal" style={{ minWidth: 0 }}>
              <FilterPopover
                actions={actions}
                config={config}
                popoverStyle={{ minWidth: 0, display: 'flex' }}
                appliedFilter={filter}
                buttonProps={{
                  variant: 'secondary',
                  size: 'sm',
                  children: (
                    <AppliedFilter
                      filter={filter}
                      config={filterConfig as SelectableFilterConfigField}
                      actions={actions}
                    />
                  ),
                }}
              />
              {index === appliedFilters.length - 1 && (
                <FilterPopover
                  actions={actions}
                  config={config}
                  trigger={IconButton}
                  buttonProps={{ icon: <IconAddPlus />, size: 'md' }}
                />
              )}
            </Stack>
          </Stack>
        );
      })}
    </Fragment>
  );
};

export function FilterPopover({
  buttonProps,
  appliedFilter,
  popoverStyle,
  trigger,
  config,
  actions,
}: {
  trigger?: PopoverButtonProps['trigger'];
  buttonProps: PopoverButtonProps['triggerProps'];
  appliedFilter?: FilterData;
  popoverStyle?: CSSObject;
  config: FilterConfig;
  actions: FilterActions;
}) {
  return (
    <PopoverButton
      trigger={trigger}
      triggerProps={{
        ...buttonProps,
        'data-testid': 'filter-button',
      }}
      contentStyle={{ maxHeight: 'none', maxWidth: FILTER_POPOVER_MAX_WIDTH }}
      popoverStyle={popoverStyle}
    >
      {({ close, isOpen }) => (
        <FilterContent
          key={String(isOpen)}
          actions={actions}
          config={config}
          initialData={appliedFilter}
          closeMenu={close}
        />
      )}
    </PopoverButton>
  );
}

export const filterSeparator: SeparatorConfig = {
  id: 'separator',
  type: 'separator',
};

const FilterSkeleton = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: pxToRem(32),
  width: pxToRem(300),
  borderRadius: pxToRem(6),
  background: `linear-gradient(
    145deg,
    ${theme.color.uiTrans10} 25%,
    ${theme.color.uiTrans20} 50%,
    ${theme.color.uiTrans10} 75%
  )`,
  backgroundSize: '200% 100%',
});

const AnimatedFilterSkeleton = styled(FilterSkeleton)`
  animation: ${backgroundSlide} 2s infinite linear;
`;
